/**
 * The site switcher's model: sites grouped by the project (client area) they
 * belong to, as `tenant → project → site` (SiteSummary.projectId), filtered
 * by what the person types. Project names come from `GET /projects/tree`;
 * a nested project reads as its full path ("Cliente › Marketing") so two
 * projects called "Sito" under different parents stay distinguishable.
 */
import type { ProjectTreeNode, SiteSummary } from '@cms/contracts'
import { displayDomain } from './live-url.js'
import { matchesQuery } from './format.js'

export interface SiteGroup {
  projectId: string
  projectName: string
  sites: SiteSummary[]
}

/** Every project id mapped to its readable path, parents first. */
export function projectPaths(nodes: readonly ProjectTreeNode[], parent = ''): Map<string, string> {
  const result = new Map<string, string>()
  for (const node of nodes) {
    const path = parent ? `${parent} › ${node.name}` : node.name
    result.set(node.id, path)
    for (const [id, childPath] of projectPaths(node.children, path)) result.set(id, childPath)
  }
  return result
}

export function groupSites(
  sites: readonly SiteSummary[],
  projectNames: ReadonlyMap<string, string>,
  query: string,
  unknownProjectName: string,
): SiteGroup[] {
  const groups = new Map<string, SiteGroup>()
  for (const site of sites) {
    const projectName = projectNames.get(site.projectId) ?? unknownProjectName
    if (!matchesQuery(query, site.name, displayDomain(site.primaryDomain), projectName)) continue
    const group = groups.get(site.projectId) ?? { projectId: site.projectId, projectName, sites: [] }
    group.sites.push(site)
    groups.set(site.projectId, group)
  }
  const collator = new Intl.Collator('it', { sensitivity: 'base' })
  return [...groups.values()]
    .map((group) => ({ ...group, sites: [...group.sites].sort((a, b) => collator.compare(a.name, b.name)) }))
    .sort((a, b) => collator.compare(a.projectName, b.projectName))
}
